// SPDX-License-Identifier: AGPL-3.0-or-later
import { useEffect, useRef, useState } from "react";
import type { IsoDate } from "@almanac/domain";
import { parentOf } from "../lib/categories.js";
import { spendingBreakdown } from "../lib/figures.js";
import { useI18n } from "../lib/i18n.js";
import type { Transaction } from "../lib/model.js";
import { Donut } from "./Donut.js";

interface Props {
  title: string;
  transactions: readonly Transaction[];
  start: IsoDate;
  end: IsoDate;
  empty: string;
}

/**
 * Spending for a date range by main category. A category with
 * sub-categories opens to show them; "Back" climbs one level.
 */
export function CategoryDonut({ title, transactions, start, end, empty }: Props) {
  const { t, category } = useI18n();
  const [parent, setParent] = useState<string | null>(null);
  const heading = useRef<HTMLHeadingElement>(null);
  const moved = useRef(false);
  const slices = spendingBreakdown(transactions, start, end, parent);

  // Keep focus in the card when the legend it was on is replaced.
  useEffect(() => {
    if (moved.current) heading.current?.focus();
    moved.current = false;
  }, [parent]);
  const go = (id: string | null) => {
    moved.current = true;
    setParent(id);
  };

  return (
    <div className="big-donut">
      <h3 ref={heading} tabIndex={-1}>
        {title}
        {parent && <span className="hint"> · {category(parent)}</span>}
      </h3>
      {parent && (
        <button type="button" className="link-button" onClick={() => go(parentOf(parent))}>
          {t("pie.back", { name: parentOf(parent) ? category(parentOf(parent)!) : t("pie.all") })}
        </button>
      )}
      <Donut
        slices={slices}
        empty={empty}
        labelFor={(id) => (id === parent ? t("budgets.rest", { name: category(id) }) : category(id))}
        onOpen={go}
      />
    </div>
  );
}
